/**
 * Pure functions for calculating qualifying pool advancement positions
 * These functions are database-independent and fully testable
 */

import { getSeedPosition } from './seeding';
import type { AdvancementPlacement } from '../types';

/**
 * Calculate seed number for a specific player from a qualifying pool
 * Uses rank-first strategy: all 1st place finishers get seeds first, then 2nd place, etc.
 *
 * @param poolNumber - 1-based pool number
 * @param playerRankInPool - 0-based rank within pool (0 = 1st place)
 * @param totalPools - Total number of qualifying pools
 * @param seededPlayers - Number of seeded players in main draw
 * @returns Calculated seed number
 */
export function calculateQualifyingSeedNumber(
  poolNumber: number,
  playerRankInPool: number,
  totalPools: number,
  seededPlayers: number,
): number {
  // Calculate seed based on rank-first strategy
  // All 1st place finishers get seeds first, then 2nd place, etc.
  // Example: 4 seeded, 4 pools, 2 per pool:
  // - Pool 1 #1: seed = 4 + (0 * 4) + 1 = 5
  // - Pool 2 #1: seed = 4 + (0 * 4) + 2 = 6
  // - Pool 1 #2: seed = 4 + (1 * 4) + 1 = 9
  const seedNumber = seededPlayers + playerRankInPool * totalPools + poolNumber;
  return seedNumber;
}

/**
 * Calculate all advancement placements for qualifying pools
 *
 * @param config - Configuration object
 * @returns Array of advancement placements
 */
export function calculateQualifyingPoolAdvancements(config: {
  totalPools: number;
  advancementsPerPool: number;
  seededPlayers: number;
  drawSize: number;
}): AdvancementPlacement[] {
  const { totalPools, advancementsPerPool, seededPlayers, drawSize } = config;

  const placements: AdvancementPlacement[] = [];

  // Generate placements for each pool and each advancing player
  for (let poolNumber = 1; poolNumber <= totalPools; poolNumber++) {
    for (let playerRank = 0; playerRank < advancementsPerPool; playerRank++) {
      const seedNumber = calculateQualifyingSeedNumber(
        poolNumber,
        playerRank,
        totalPools,
        seededPlayers,
      );

      // Get tournament position for this seed
      const tournamentPosition = getSeedPosition(seedNumber, drawSize);

      if (!tournamentPosition) {
        throw new Error(
          `Invalid seed number ${seedNumber} for pool ${poolNumber}, rank ${playerRank} in draw size ${drawSize}`,
        );
      }

      // Convert tournament position to match index and player position
      const matchIndex = Math.floor((tournamentPosition - 1) / 2);
      const playerPosition = (tournamentPosition - 1) % 2 === 0 ? 'player1' : 'player2';

      placements.push({
        poolNumber,
        playerRank,
        seedNumber,
        tournamentPosition,
        matchIndex,
        playerPosition,
        separationApplied: false,
      });
    }
  }

  return placements;
}

/**
 * Apply separation logic to avoid same-pool players meeting in round 1
 *
 * @param placements - Initial placements array
 * @param drawSize - Draw size
 * @returns Modified placements array with separation applied
 */
export function applySeparationLogic(
  placements: AdvancementPlacement[],
  drawSize: number,
): AdvancementPlacement[] {
  // Create a copy to avoid mutating the original
  const separatedPlacements = [...placements];

  // Group placements by match index to identify potential conflicts
  const matchGroups = new Map<number, AdvancementPlacement[]>();

  separatedPlacements.forEach((placement) => {
    const matchIndex = placement.matchIndex;
    if (!matchGroups.has(matchIndex)) {
      matchGroups.set(matchIndex, []);
    }
    matchGroups.get(matchIndex)!.push(placement);
  });

  // Find matches where both players are from the same pool
  const conflicts: Array<{
    matchIndex: number;
    poolNumber: number;
    placements: AdvancementPlacement[];
  }> = [];

  matchGroups.forEach((placementsInMatch, matchIndex) => {
    if (placementsInMatch.length === 2) {
      const [placement1, placement2] = placementsInMatch;
      if (placement1.poolNumber === placement2.poolNumber) {
        conflicts.push({
          matchIndex,
          poolNumber: placement1.poolNumber,
          placements: placementsInMatch,
        });
      }
    }
  });

  // Resolve conflicts by swapping one player to a different position
  for (const conflict of conflicts) {
    const { matchIndex: conflictMatchIndex, poolNumber, placements: conflictPlacements } = conflict;

    // Find a suitable swap candidate
    const swapCandidate = findSwapCandidate(
      separatedPlacements,
      conflictMatchIndex,
      poolNumber,
      drawSize,
    );

    if (swapCandidate) {
      // Perform the swap
      const playerToMove = conflictPlacements[0]; // Move the first player
      const targetPlacement = separatedPlacements.find(
        (p) =>
          p.matchIndex === swapCandidate.matchIndex &&
          p.playerPosition === swapCandidate.playerPosition,
      );

      if (targetPlacement) {
        // Swap the match indices and player positions
        const tempMatchIndex = playerToMove.matchIndex;
        const tempPlayerPosition = playerToMove.playerPosition;

        playerToMove.matchIndex = targetPlacement.matchIndex;
        playerToMove.playerPosition = targetPlacement.playerPosition;
        playerToMove.separationApplied = true;

        targetPlacement.matchIndex = tempMatchIndex;
        targetPlacement.playerPosition = tempPlayerPosition;
        targetPlacement.separationApplied = true;

        console.log(
          `[SEPARATION] Swapped pool ${poolNumber} players: match ${tempMatchIndex} ↔ match ${targetPlacement.matchIndex}`,
        );
      }
    } else {
      // If no swap candidate found, try to move to an empty position
      const emptyPosition = findEmptyPosition(separatedPlacements, drawSize);
      if (emptyPosition) {
        const playerToMove = conflictPlacements[0];
        playerToMove.matchIndex = emptyPosition.matchIndex;
        playerToMove.playerPosition = emptyPosition.playerPosition;
        playerToMove.separationApplied = true;

        console.log(
          `[SEPARATION] Moved pool ${poolNumber} player to empty position: match ${emptyPosition.matchIndex}`,
        );
      } else {
        console.warn(
          `[SEPARATION] Could not find suitable swap for pool ${poolNumber} conflict at match ${conflictMatchIndex}`,
        );
      }
    }
  }

  return separatedPlacements;
}

/**
 * Find a suitable swap candidate to resolve a same-pool conflict
 *
 * @param placements - All placements
 * @param conflictMatchIndex - Match index with the conflict
 * @param poolNumber - Pool number involved in the conflict
 * @param drawSize - Draw size
 * @returns Swap candidate or null if none found
 */
function findSwapCandidate(
  placements: AdvancementPlacement[],
  conflictMatchIndex: number,
  poolNumber: number,
  drawSize: number,
): { matchIndex: number; playerPosition: 'player1' | 'player2' } | null {
  const totalMatches = drawSize / 2;

  // Look for empty positions or positions with players from different pools
  for (let matchIndex = 0; matchIndex < totalMatches; matchIndex++) {
    if (matchIndex === conflictMatchIndex) continue;

    const placementsInMatch = placements.filter((p) => p.matchIndex === matchIndex);

    // If match is empty, it's a good candidate
    if (placementsInMatch.length === 0) {
      return { matchIndex, playerPosition: 'player1' };
    }

    // If match has only one player and it's from a different pool, it's a good candidate
    if (placementsInMatch.length === 1) {
      const existingPlayer = placementsInMatch[0];
      if (existingPlayer.poolNumber !== poolNumber) {
        return { matchIndex, playerPosition: 'player2' };
      }
    }

    // If match has two players from different pools, we can swap with one of them
    if (placementsInMatch.length === 2) {
      const [player1, player2] = placementsInMatch;
      // Find a player from a different pool to swap with
      if (player1.poolNumber !== poolNumber) {
        return { matchIndex, playerPosition: 'player1' };
      }
      if (player2.poolNumber !== poolNumber) {
        return { matchIndex, playerPosition: 'player2' };
      }
    }
  }

  return null;
}

/**
 * Find an empty position in the draw
 *
 * @param placements - All placements
 * @param drawSize - Draw size
 * @returns Empty position or null if none found
 */
function findEmptyPosition(
  placements: AdvancementPlacement[],
  drawSize: number,
): { matchIndex: number; playerPosition: 'player1' | 'player2' } | null {
  const totalMatches = drawSize / 2;

  // Look for empty positions
  for (let matchIndex = 0; matchIndex < totalMatches; matchIndex++) {
    const placementsInMatch = placements.filter((p) => p.matchIndex === matchIndex);

    // If match is empty, return player1 position
    if (placementsInMatch.length === 0) {
      return { matchIndex, playerPosition: 'player1' };
    }

    // If match has only one player, return the other position
    if (placementsInMatch.length === 1) {
      const existingPosition = placementsInMatch[0].playerPosition;
      return { matchIndex, playerPosition: existingPosition === 'player1' ? 'player2' : 'player1' };
    }
  }

  return null;
}
